import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, ApiFehler, token } from '../../../gemeinsam/lib/api';
import { useDaten, euro, preis, menge as mengeText, datumZeit, profil } from '../../../gemeinsam/lib/daten';
import type { Artikel, Bewegung, Lieferant } from '../../../gemeinsam/lib/typen';
import {
  Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner,
} from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import EinheitWahl from '../komponenten/EinheitWahl';
import BestandDialog from '../komponenten/BestandDialog';
import { useAuth } from '../../../gemeinsam/lib/AuthContext';
import { einheitAnzeige } from '../lib/einheiten';

const LEER = {
  name: '', artikelnummer: '', ean: '', beschreibung: '', kategorie: 'Allgemein',
  einheit: 'Stück', lagerort: '', einkaufspreis: '0', verkaufspreis: '0',
  steuersatz: '19', mindestbestand: '0', lieferantId: '', anfangsbestand: '0',
};

type Antwort = { produkte: Artikel[]; kategorien: string[] };

export default function Lager() {
  const { t } = useSprache();
  const [suche, setSuche] = useState('');
  const [kategorie, setKategorie] = useState('');
  // Die Übersicht verlinkt „unter Mindestbestand" als /lager?knapp=1.
  const ort = useLocation();
  const [nurKnapp, setNurKnapp] = useState(() => new URLSearchParams(ort.search).get('knapp') === '1');

  const abfrage = [
    suche ? `suche=${encodeURIComponent(suche)}` : '',
    kategorie ? `kategorie=${encodeURIComponent(kategorie)}` : '',
    nurKnapp ? 'knapp=1' : '',
  ].filter(Boolean).join('&');

  const { daten, laedt, fehler, neuLaden } = useDaten<Antwort>(
    `/products${abfrage ? '?' + abfrage : ''}`, [suche, kategorie, nurKnapp]);
  const { daten: lieferanten } = useDaten<Lieferant[]>('/suppliers');
  const { daten: formate } = useDaten<Array<{ id: string; name: string; proBogen: number; beschreibung: string; einzelblatt: boolean }>>('/labels/formate');

  const [formular, setFormular] = useState<typeof LEER & { id?: string } | null>(null);
  const [buchung, setBuchung] = useState<{ artikel: Artikel; richtung: 1 | -1 } | null>(null);
  const { istAdmin } = useAuth();
  const [detail, setDetail] = useState<(Artikel & { bewegungen: Bewegung[] }) | null>(null);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  // Etiketten: Auswahl per Haken, dann Format und Umfang wählen.
  const [labelOffen, setLabelOffen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [labelFormat, setLabelFormat] = useState('universal');
  const [labelAnzahl, setLabelAnzahl] = useState('1');
  const [mitPreis, setMitPreis] = useState(true);
  const [mitFirma, setMitFirma] = useState(false);
  const [mitEinheit, setMitEinheit] = useState(true);

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    if (!formular) return;
    setSendeFehler(null);
    setSendet(true);
    try {
      const { id, anfangsbestand, ...rest } = formular;
      const felder = {
        ...rest,
        einkaufspreis: Number(rest.einkaufspreis.replace(',', '.')) || 0,
        verkaufspreis: Number(rest.verkaufspreis.replace(',', '.')) || 0,
        steuersatz: Number(rest.steuersatz) || 0,
        mindestbestand: Number(rest.mindestbestand) || 0,
        lieferantId: rest.lieferantId || null,
      };
      if (id) await api(`/products/${id}`, { methode: 'PUT', daten: felder });
      else await api('/products', { methode: 'POST', daten: { ...felder, anfangsbestand: Number(anfangsbestand) || 0 } });
      setFormular(null);
      setMeldung(id ? 'Artikel gespeichert.' : 'Artikel angelegt.');
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSendet(false);
    }
  }

  // Bestand buchen im Dialog (BestandDialog) — früher zwei Browser-Abfragen,
  // ohne Lieferant, ohne Preis, ohne Rechnung.
  function buchen(artikel: Artikel, richtung: 1 | -1) {
    setBuchung({ artikel, richtung });
    setMeldung(null);
    setSendeFehler(null);
  }


  // Die Druckansicht liegt hinter der Anmeldung — ein einfacher Link kann
  // keinen Authorization-Header mitschicken.
  // Einzeldruck und Sammeldruck gehen denselben Weg — nur die Auswahl und die
  // Stückzahl unterscheiden sich.
  async function etikettenDrucken(nurDieser?: Artikel, stueck?: string) {
    setSendeFehler(null);
    const abfrage = new URLSearchParams({
      format: labelFormat,
      anzahl: stueck ?? labelAnzahl,
      preis: mitPreis ? '1' : '0',
      firma: mitFirma ? '1' : '0',
      einheit: mitEinheit ? '1' : '0',
    });
    if (nurDieser) abfrage.set('ids', nurDieser.id);
    else if (gewaehlt.length) abfrage.set('ids', gewaehlt.join(','));

    try {
      const antwort = await fetch(apiUrl(`/api/labels/druck?${abfrage}`), {
        headers: { Authorization: `Bearer ${token() ?? ''}` },
      });
      if (!antwort.ok) throw new Error(String(antwort.status));
      const html = await antwort.text();
      const fenster = window.open('', '_blank');
      if (!fenster) {
        setSendeFehler('Das Fenster wurde blockiert. Bitte Pop-ups für diese Seite erlauben.');
        return;
      }
      fenster.document.write(html);
      fenster.document.close();
    } catch {
      setSendeFehler('Etiketten konnten nicht erzeugt werden.');
    }
  }

  async function einzelEtikett(artikel: Artikel) {
    const eingabe = prompt(
      `Etikett für „${artikel.name}"\n\nWie viele Stück?`, '1');
    if (eingabe === null) return;
    const anzahl = Math.min(Math.max(Number(eingabe.replace(/\D/g, '')) || 1, 1), 100);
    await etikettenDrucken(artikel, String(anzahl));
  }

  async function detailsZeigen(artikel: Artikel) {
    try {
      setDetail(await api(`/products/${artikel.id}`));
    } catch { setSendeFehler('Details konnten nicht geladen werden.'); }
  }

  const produkte = daten?.produkte ?? [];
  const lagerwert = produkte.reduce((s, p) => s + (p.bestand || 0) * (p.einkaufspreis || 0), 0);
  const knapp = produkte.filter(p => (p.bestand ?? 0) <= (p.mindestbestand ?? 0)).length;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('lager.titel')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {produkte.length} {t('lager.anzahlArtikel')} · {t('uebersicht.lagerwert')} {euro(lagerwert)}
            {knapp > 0 && <span className="text-amber-600 dark:text-amber-400"> · {knapp} unter Mindestbestand</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {!labelOffen && (
            <Knopf onClick={() => { setLabelOffen(true); setMeldung(null); }}>
              {t('lager.etiketten')}{gewaehlt.length > 0 && ` (${gewaehlt.length})`}
            </Knopf>
          )}
          {!formular && (
            <Knopf variante="primaer" onClick={() => { setFormular({ ...LEER }); setMeldung(null); }}>
              {t('lager.neu')}
            </Knopf>
          )}
        </div>
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {sendeFehler && <Hinweis art="fehler">{sendeFehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />
      {buchung && (
        <BestandDialog key={`${buchung.artikel.id}-${buchung.richtung}`} artikel={buchung.artikel} richtung={buchung.richtung}
                       lieferanten={lieferanten ?? []} istAdmin={istAdmin}
                       beiAbbruch={() => setBuchung(null)}
                       beiFertig={async text => { setBuchung(null); setMeldung(text); await neuLaden(); }} />
      )}

      {formular && (
        <Karte>
          <KartenKopf titel={formular.id ? 'Artikel bearbeiten' : 'Neuer Artikel'}
                      hinweis={formular.id ? 'Der Bestand wird über Buchungen geändert, nicht hier.' : undefined} />
          <form onSubmit={speichern} className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Feld label={t('feld.bezeichnung')} value={formular.name} required autoFocus
                    onChange={e => setFormular(f => f && ({ ...f, name: e.target.value }))} />
              <Feld label={t('lager.artikelnummer')} value={formular.artikelnummer}
                    onChange={e => setFormular(f => f && ({ ...f, artikelnummer: e.target.value }))} />
              <Feld label={t('lager.ean')} value={formular.ean}
                    onChange={e => setFormular(f => f && ({ ...f, ean: e.target.value }))} />
              <Feld label={t('lager.kategorie')} value={formular.kategorie}
                    onChange={e => setFormular(f => f && ({ ...f, kategorie: e.target.value }))} />
              <EinheitWahl wert={formular.einheit}
                           beiAenderung={e => setFormular(f => f && ({ ...f, einheit: e }))} />
              <Feld label={t('lager.lagerort')} value={formular.lagerort}
                    onChange={e => setFormular(f => f && ({ ...f, lagerort: e.target.value }))} />
              <Feld label={t('lager.einkaufspreis')} value={formular.einkaufspreis} inputMode="decimal"
                    onChange={e => setFormular(f => f && ({ ...f, einkaufspreis: e.target.value }))} />
              <Feld label={t('lager.verkaufspreis')} value={formular.verkaufspreis} inputMode="decimal"
                    onChange={e => setFormular(f => f && ({ ...f, verkaufspreis: e.target.value }))} />
              <Auswahl label={profil().steuerName} value={formular.steuersatz}
                       onChange={e => setFormular(f => f && ({ ...f, steuersatz: e.target.value }))}>
                <option value="19">19 %</option>
                <option value="7">7 %</option>
                <option value="0">0 %</option>
              </Auswahl>
              <Feld label={t('lager.mindestbestand')} value={formular.mindestbestand} inputMode="numeric"
                    onChange={e => setFormular(f => f && ({ ...f, mindestbestand: e.target.value }))} />
              <Auswahl label={t('feld.lieferant')} value={formular.lieferantId}
                       onChange={e => setFormular(f => f && ({ ...f, lieferantId: e.target.value }))}>
                <option value="">{t('lager.keinLieferant')}</option>
                {(lieferanten ?? []).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Auswahl>
              {!formular.id && (
                <Feld label={t('lager.anfangsbestand')} value={formular.anfangsbestand} inputMode="decimal"
                      hinweis={t('lager.wareneingang')}
                      onChange={e => setFormular(f => f && ({ ...f, anfangsbestand: e.target.value }))} />
              )}
            </div>
            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={sendet}>{t('aktion.speichern')}</Knopf>
              <Knopf type="button" onClick={() => { setFormular(null); setSendeFehler(null); }}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </form>
        </Karte>
      )}

      {labelOffen && (
        <Karte>
          <KartenKopf
            titel={t('lager.etikettenDrucken')}
            hinweis={gewaehlt.length
              ? `${gewaehlt.length} ${gewaehlt.length === 1 ? 'Artikel' : 'Artikel'} ausgewählt`
              : 'Ohne Auswahl werden alle aktiven Artikel gedruckt'}
            aktion={<Knopf variante="still" onClick={() => setLabelOffen(false)}>{t('aktion.schliessen')}</Knopf>}
          />
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Auswahl label={t('lager.format')} value={labelFormat} onChange={e => setLabelFormat(e.target.value)}>
                {(formate ?? []).map(f => (
                  <option key={f.id} value={f.id}>
                    {f.name}{f.einzelblatt ? '' : ` · ${f.proBogen} je Bogen`}
                  </option>
                ))}
              </Auswahl>
              <Feld label={t('lager.stueckJeArtikel')} value={labelAnzahl} inputMode="numeric"
                    hinweis={t('lager.mehrfachHinweis')}
                    onChange={e => setLabelAnzahl(e.target.value.replace(/\D/g, '') || '1')} />
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              {(formate ?? []).find(f => f.id === labelFormat)?.beschreibung}
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-slate-700 dark:text-slate-300">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={mitPreis} onChange={e => setMitPreis(e.target.checked)} />
                Verkaufspreis
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={mitEinheit} onChange={e => setMitEinheit(e.target.checked)} />
                Einheit
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={mitFirma} onChange={e => setMitFirma(e.target.checked)} />
                Firmenname
              </label>
            </div>

            <Hinweis art="info">
              Für ein einzelnes Etikett genügt der Knopf „Etikett" in der Artikelzeile — dieser
              Bereich ist für größere Mengen.
              Auf dem Etikett stehen Bezeichnung, Artikelnummer, Lagerort und ein Strichcode —
              als EAN-13, wenn eine gültige EAN hinterlegt ist, sonst die Artikelnummer als
              Code 128. Beide werden von handelsüblichen Scannern gelesen.
            </Hinweis>

            <div className="flex flex-wrap gap-2">
              <Knopf variante="primaer" onClick={() => etikettenDrucken()}>
                {gewaehlt.length
                  ? `${gewaehlt.length * (Number(labelAnzahl) || 1)} Etiketten erzeugen`
                  : 'Alle Artikel drucken'}
              </Knopf>
              {gewaehlt.length > 0 && (
                <Knopf onClick={() => setGewaehlt([])}>{t('lager.auswahlAufheben')}</Knopf>
              )}
              <Knopf onClick={() => setGewaehlt(produkte.filter(p => p.aktiv !== false).map(p => p.id))}>
                Alle sichtbaren auswählen
              </Knopf>
            </div>
          </div>
        </Karte>
      )}

      {detail && (
        <Karte>
          <KartenKopf titel={`Bestandsverlauf: ${detail.name}`}
                      aktion={<Knopf variante="still" onClick={() => setDetail(null)}>{t('aktion.schliessen')}</Knopf>} />
          {detail.bewegungen.length === 0 ? (
            <LeerZustand titel={t('lager.keineBewegungen')} />
          ) : (
            <ul className="divide-y divide-slate-200 text-sm dark:divide-slate-800">
              {detail.bewegungen.map(b => (
                <li key={b.id} className="flex items-center gap-3 px-5 py-2.5">
                  <span className={`w-16 font-medium ${b.menge > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                    {b.menge > 0 ? '+' : ''}{mengeText(b.menge)}
                  </span>
                  <span className="flex-1 text-slate-700 dark:text-slate-300">
                    {b.art}{b.grund ? ` · ${b.grund}` : ''}
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">→ {mengeText(b.bestandNachher)}</span>
                  <span className="w-32 text-right text-xs text-slate-500 dark:text-slate-400">{datumZeit(b.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Karte>
      )}

      <Karte>
        <div className="grid gap-3 border-b border-slate-200 p-4 sm:grid-cols-[1fr_200px_auto] sm:items-end dark:border-slate-800">
          <Feld label={t('allg.suche')} value={suche} placeholder={t('lager.suche')}
                onChange={e => setSuche(e.target.value)} />
          <Auswahl label={t('lager.kategorie')} value={kategorie} onChange={e => setKategorie(e.target.value)}>
            <option value="">{t('lager.alle')}</option>
            {(daten?.kategorien ?? []).map(k => <option key={k} value={k}>{k}</option>)}
          </Auswahl>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={nurKnapp} onChange={e => setNurKnapp(e.target.checked)} />
            {t('lager.nurKnappe')}
          </label>
        </div>

        {laedt && !daten ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : produkte.length === 0 ? (
          <LeerZustand titel={t('lager.leer')} text={t('lager.leerText')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  {labelOffen && <th className="w-10 px-3 py-2.5"><span className="sr-only">Auswahl</span></th>}
                  <th className="px-5 py-2.5 font-medium">Artikel</th>
                  <th className="px-3 py-2.5 font-medium">{t('feld.bestand')}</th>
                  <th className="hidden px-3 py-2.5 text-right font-medium sm:table-cell">EK</th>
                  <th className="px-3 py-2.5 text-right font-medium">VK</th>
                  <th className="px-5 py-2.5 text-right font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {produkte.map(p => {
                  const wenig = (p.bestand ?? 0) <= (p.mindestbestand ?? 0);
                  return (
                    <tr key={p.id} className={p.aktiv === false ? 'opacity-50' : ''}>
                      {labelOffen && (
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={gewaehlt.includes(p.id)}
                            aria-label={`${p.name} für Etikett auswählen`}
                            onChange={e => setGewaehlt(g => e.target.checked
                              ? [...g, p.id]
                              : g.filter(x => x !== p.id))}
                          />
                        </td>
                      )}
                      <td className="px-5 py-3">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{p.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {[p.artikelnummer, p.kategorie, p.lagerort].filter(Boolean).join(' · ')}
                        </p>
                      </td>
                      <td className="px-3 py-3">
                        <span className={wenig ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}>
                          {mengeText(p.bestand ?? 0, einheitAnzeige(p.einheit, t))}
                        </span>
                        {wenig && <Abzeichen art="gelb">{t('lager.knapp')}</Abzeichen>}
                      </td>
                      <td className="hidden px-3 py-3 text-right text-slate-600 dark:text-slate-400 sm:table-cell">{preis(p.einkaufspreis)}</td>
                      <td className="px-3 py-3 text-right font-medium text-slate-900 dark:text-slate-100">
                        {preis(p.verkaufspreis)}
                        {p.einheit && p.einheit !== 'Stück' && (
                          <span className="font-normal text-slate-400"> / {einheitAnzeige(p.einheit, t)}</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={() => buchen(p, 1)} title="Wareneingang">+</Knopf>
                          <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={() => buchen(p, -1)} title="Abgang">−</Knopf>
                          <Knopf variante="still" className="!px-2 !py-1 !text-xs"
                                 title="Etikett für diesen Artikel drucken"
                                 onClick={() => einzelEtikett(p)}>{t('lager.etikett')}</Knopf>
                          <Knopf variante="still" className="!px-2 !py-1 !text-xs" onClick={() => detailsZeigen(p)}>{t('lager.verlauf')}</Knopf>
                          <Knopf variante="still" className="!px-2 !py-1 !text-xs"
                                 onClick={() => { setFormular({ ...LEER, ...p, id: p.id,
                                   einkaufspreis: String(p.einkaufspreis), verkaufspreis: String(p.verkaufspreis),
                                   steuersatz: String(p.steuersatz), mindestbestand: String(p.mindestbestand),
                                   lieferantId: p.lieferantId ?? '', anfangsbestand: '0' }); }}>
                            Bearbeiten
                          </Knopf>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Karte>
    </div>
  );
}
